/**
 * Subscription Invoice Template Renderer
 * Follows the same pattern as quotationRenderer.ts and classicRenderer.ts
 */

import { renderTemplate } from "../templateRenderer";
import {
  SUBSCRIPTION_INVOICE_TEMPLATE,
  SUBSCRIPTION_INVOICE_CSS,
} from "./loader";

export interface SubscriptionInvoiceContext {
  payment: {
    id?: string | number;
    due_date?: string;
    billing_period_start?: string;
    billing_period_end?: string;
    package_name?: string;
    amount?: number | string;
    additional_amount?: number | string;
    total_amount?: number | string;
    paid_at?: string;
    payment_method?: string;
    reference?: string;
    note?: string;
  };
  tenant?: {
    name?: string;
    sub_name?: string;
    email?: string;
    phone?: string;
  };
  provider: {
    name: string;
    address?: string;
    phone?: string;
    email?: string;
    logo_url?: string;
  };
  currency?: string;
  isForPrint?: boolean;
}

const BASE_TEMPLATE = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8"/>
  <meta name="viewport" content="width=device-width,initial-scale=1"/>
  <title>{{ title }}</title>
  <style>{{ styles }}</style>
</head>
<body>
  {{ content | safe }}
</body>
</html>`;

function fmtDate(d?: string): string {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function buildCssVariables(isForPrint: boolean): string {
  return `
    :root {
      --pi-bg: ${isForPrint ? "#fff" : "#edf2f7"};
      --pi-body-pad: ${isForPrint ? "0" : "28px 20px"};
      --pi-radius: ${isForPrint ? "0" : "6px"};
      --pi-shadow: ${isForPrint ? "none" : "0 4px 24px rgba(0,0,0,0.10)"};
      --pi-border: ${isForPrint ? "1px solid #e5e7eb" : "1px solid #cbd5e1"};
      --pi-width: 210mm;
    }
  `;
}

export function renderSubscriptionInvoice(
  ctx: SubscriptionInvoiceContext,
): string {
  const { payment, tenant, provider, currency = "LKR", isForPrint = false } = ctx;

  const baseAmount = Number(payment.amount) || 0;
  const additionalAmount = Number(payment.additional_amount) || 0;
  const totalAmount =
    Number(payment.total_amount) || baseAmount + additionalAmount;

  const billingPeriod =
    payment.billing_period_start && payment.billing_period_end
      ? `${payment.billing_period_start} - ${payment.billing_period_end}`
      : fmtDate(payment.due_date);

  // If the invoice id is a UUID (long with dashes), shorten it for the Invoice No header display, e.g. INV-A1B2C3D4
  const rawId = String(payment.id || "0");
  const displayId =
    rawId.includes("-") && rawId.length > 15
      ? rawId.substring(0, 8).toUpperCase()
      : rawId.padStart(6, "0");
  const invoiceNo = `INV-${displayId}`;

  const contentContext = {
    // Provider (the POS company)
    providerName: provider.name,
    providerAddress: provider.address || "",
    providerPhone: provider.phone || "",
    providerEmail: provider.email || "",
    logoUrl: provider.logo_url || "",

    // Invoice meta
    invoiceNo,
    issuedDate: new Date().toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    }),
    dueDate: fmtDate(payment.due_date),
    billingPeriod,
    paymentMethod: payment.payment_method || "—",
    paidAt: payment.paid_at
      ? new Date(payment.paid_at).toLocaleString()
      : "",
    reference: payment.reference || "—",
    generatedAt: new Date().toLocaleString(),

    // Tenant (billed to)
    tenantName: tenant?.name || "Tenant",
    tenantSubName: tenant?.sub_name || "",
    tenantEmail: tenant?.email || "",
    tenantPhone: tenant?.phone || "",

    // Package
    packageName: payment.package_name || "Subscription Package",

    // Amounts
    currency,
    baseAmount,
    additionalAmount,
    totalAmount,

    // Note
    note: payment.note || "",
  };

  const content = renderTemplate(SUBSCRIPTION_INVOICE_TEMPLATE, contentContext);
  const styles = buildCssVariables(isForPrint) + "\n" + SUBSCRIPTION_INVOICE_CSS;

  return renderTemplate(BASE_TEMPLATE, {
    title: invoiceNo,
    styles,
    content,
  });
}
